import { FontId, FontSize, FontWeight } from '../types'

export type FontCategory = 'sans' | 'serif' | 'display' | 'mono'

export interface FontOption {
  id: FontId
  name: string
  category: FontCategory
  googleFamily?: string
  fallback: string
  stack: string[]
  weights: number[]
}

const SANS_FALLBACK = "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const SERIF_FALLBACK = "ui-serif, Georgia, Cambria, 'Times New Roman', serif"
const MONO_FALLBACK =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"

const SANS_STACK = ['system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'sans-serif']
const SERIF_STACK = ['ui-serif', 'Georgia', 'Cambria', 'Times New Roman', 'serif']
const MONO_STACK = [
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Monaco',
  'Consolas',
  'Liberation Mono',
  'Courier New',
  'monospace',
]

export const FONT_OPTIONS: FontOption[] = [
  {
    id: 'system',
    name: 'System UI',
    category: 'sans',
    fallback: SANS_FALLBACK,
    stack: SANS_STACK,
    weights: [300, 400, 500, 600, 700, 800, 900],
  },
  {
    id: 'inter',
    name: 'Inter',
    category: 'sans',
    googleFamily: 'Inter',
    fallback: SANS_FALLBACK,
    stack: ['Inter', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'roboto',
    name: 'Roboto',
    category: 'sans',
    googleFamily: 'Roboto',
    fallback: SANS_FALLBACK,
    stack: ['Roboto', ...SANS_STACK],
    weights: [300, 400, 500, 700, 900],
  },
  {
    id: 'open-sans',
    name: 'Open Sans',
    category: 'sans',
    googleFamily: 'Open Sans',
    fallback: SANS_FALLBACK,
    stack: ['Open Sans', ...SANS_STACK],
    weights: [300, 400, 600, 700, 800],
  },
  {
    id: 'lato',
    name: 'Lato',
    category: 'sans',
    googleFamily: 'Lato',
    fallback: SANS_FALLBACK,
    stack: ['Lato', ...SANS_STACK],
    weights: [300, 400, 700, 900],
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    category: 'sans',
    googleFamily: 'Montserrat',
    fallback: SANS_FALLBACK,
    stack: ['Montserrat', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'poppins',
    name: 'Poppins',
    category: 'display',
    googleFamily: 'Poppins',
    fallback: SANS_FALLBACK,
    stack: ['Poppins', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'nunito',
    name: 'Nunito',
    category: 'sans',
    googleFamily: 'Nunito',
    fallback: SANS_FALLBACK,
    stack: ['Nunito', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'raleway',
    name: 'Raleway',
    category: 'display',
    googleFamily: 'Raleway',
    fallback: SANS_FALLBACK,
    stack: ['Raleway', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'work-sans',
    name: 'Work Sans',
    category: 'sans',
    googleFamily: 'Work Sans',
    fallback: SANS_FALLBACK,
    stack: ['Work Sans', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'source-sans-3',
    name: 'Source Sans 3',
    category: 'sans',
    googleFamily: 'Source Sans 3',
    fallback: SANS_FALLBACK,
    stack: ['Source Sans 3', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'dm-sans',
    name: 'DM Sans',
    category: 'sans',
    googleFamily: 'DM Sans',
    fallback: SANS_FALLBACK,
    stack: ['DM Sans', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'playfair-display',
    name: 'Playfair Display',
    category: 'serif',
    googleFamily: 'Playfair Display',
    fallback: SERIF_FALLBACK,
    stack: ['Playfair Display', ...SERIF_STACK],
    weights: [400, 500, 600, 700, 800, 900],
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    category: 'serif',
    googleFamily: 'Merriweather',
    fallback: SERIF_FALLBACK,
    stack: ['Merriweather', ...SERIF_STACK],
    weights: [300, 400, 700, 900],
  },
  {
    id: 'libre-baskerville',
    name: 'Libre Baskerville',
    category: 'serif',
    googleFamily: 'Libre Baskerville',
    fallback: SERIF_FALLBACK,
    stack: ['Libre Baskerville', ...SERIF_STACK],
    weights: [400, 700],
  },
  {
    id: 'roboto-slab',
    name: 'Roboto Slab',
    category: 'serif',
    googleFamily: 'Roboto Slab',
    fallback: SERIF_FALLBACK,
    stack: ['Roboto Slab', ...SERIF_STACK],
    weights: [300, 400, 500, 700, 900],
  },
  {
    id: 'dm-serif-display',
    name: 'DM Serif Display',
    category: 'serif',
    googleFamily: 'DM Serif Display',
    fallback: SERIF_FALLBACK,
    stack: ['DM Serif Display', ...SERIF_STACK],
    weights: [400],
  },
  {
    id: 'space-grotesk',
    name: 'Space Grotesk',
    category: 'display',
    googleFamily: 'Space Grotesk',
    fallback: SANS_FALLBACK,
    stack: ['Space Grotesk', ...SANS_STACK],
    weights: [300, 400, 500, 600, 700],
  },
  {
    id: 'jetbrains-mono',
    name: 'JetBrains Mono',
    category: 'mono',
    googleFamily: 'JetBrains Mono',
    fallback: MONO_FALLBACK,
    stack: ['JetBrains Mono', ...MONO_STACK],
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    id: 'ibm-plex-mono',
    name: 'IBM Plex Mono',
    category: 'mono',
    googleFamily: 'IBM Plex Mono',
    fallback: MONO_FALLBACK,
    stack: ['IBM Plex Mono', ...MONO_STACK],
    weights: [300, 400, 500, 600, 700],
  },
  {
    id: 'space-mono',
    name: 'Space Mono',
    category: 'mono',
    googleFamily: 'Space Mono',
    fallback: MONO_FALLBACK,
    stack: ['Space Mono', ...MONO_STACK],
    weights: [400, 700],
  },
]

export const FONT_OPTION_BY_ID = Object.fromEntries(
  FONT_OPTIONS.map((font) => [font.id, font])
) as Record<FontId, FontOption>

export const FONT_WEIGHT_VALUES: Record<FontWeight, number> = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
}

export const FONT_WEIGHT_OPTIONS: { value: FontWeight; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'regular', label: 'Regular' },
  { value: 'medium', label: 'Medium' },
  { value: 'semibold', label: 'Semibold' },
  { value: 'bold', label: 'Bold' },
  { value: 'extrabold', label: 'Extra Bold' },
  { value: 'black', label: 'Black' },
]

export const FONT_SIZE_VALUES: Record<FontSize, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
}

export const FONT_SIZE_OPTIONS: { value: FontSize; label: string }[] = [
  { value: 'xs', label: 'XS · 12px' },
  { value: 'sm', label: 'SM · 14px' },
  { value: 'base', label: 'Base · 16px' },
  { value: 'lg', label: 'LG · 18px' },
  { value: 'xl', label: 'XL · 20px' },
  { value: '2xl', label: '2XL · 24px' },
  { value: '3xl', label: '3XL · 30px' },
  { value: '4xl', label: '4XL · 36px' },
  { value: '5xl', label: '5XL · 48px' },
  { value: '6xl', label: '6XL · 60px' },
  { value: '7xl', label: '7XL · 72px' },
]

export const getFontOption = (id: FontId): FontOption => FONT_OPTION_BY_ID[id]

export const getFontFamilyValue = (id: FontId): string => {
  const font = getFontOption(id)
  if (!font.googleFamily) {
    return font.fallback
  }
  return `'${font.googleFamily}', ${font.fallback}`
}

export const getFontFamilyStack = (id: FontId): string[] => {
  const font = getFontOption(id)
  return font.stack
}
